import quickfireModel from "quickfire/model/quickfire-model";
import { QuickfireCreditType } from "quickfire/model/quickfire-model";
import { Signal } from "signals";
import Components from "slotworks/components/components";
import PromptContent from "slotworks/components/prompt/prompt-content";
import Layers from "slotworks/graphics/layers/layers";
import BetModel from "slotworks/model/bets/bet-model";
import GameState from "slotworks/model/game-state";
import CommsManager from "slotworks/server/comms-manager";
import { Services } from "slotworks/services/services";
import ControlOptions from "slotworks/smart-ui/options/control-options";
import SmartUIFlags from "slotworks/smart-ui/smart-ui-flags";
import smartUiFlags, { SmartUIFlag } from "slotworks/smart-ui/smart-ui-flags";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import Contract from "slotworks/utils/contracts/contract";
import Timer from "slotworks/utils/timer";
import ValueList from "slotworks/utils/value-list";

class QuickfireFreeGamesComponent {

    public onDiscard: Signal = new Signal();

    private layer: Layers;

    private savedCreditSizes: ValueList<number>;
    private savedCreditsPerLine: ValueList<number>;

    private expireDuringPromptSafeguardTimer: number;

    public init() {
        SmartUIFlags.onFlagsUpdated.add(() => this.checkComplete());
    }

    /**
     * Opens the intro page. Always the entry point.
     */
    public intro() {
        this.layer = Layers.Custom.get("QuickfireFreeGames");

        const haveFreeGames = quickfireModel.freeGames && quickfireModel.freeGames[0];
        const recover = (haveFreeGames && quickfireModel.freeGames[0].gamesPlayed > 0) && !quickfireModel.playingFreeGames;

        if (haveFreeGames) {
            const freeGame = quickfireModel.freeGames[0];

            if (freeGame.gamesRemaining === 0) {
                if (!GameState.getCurrentGame().isRecovered) {
                    return this.summary();
                } else {
                    return Contract.empty();
                }
            }

            if (freeGame.unixEndTime === Infinity) {
                return this.expire();
            }
        }

        if (GameState.getCurrentGame().isRecovered && !quickfireModel.nonFreeGamesRecoveryCompleted) {
            if (recover) {
                this.start();
            } else {
                quickfireModel.showFreeGameOffers = true;
            }
            return Contract.empty();
        }

        return this.freeGamesPopup("freegames_intro", recover);
    }

    /**
     * Updates remaining count
     */
    public update() {
        if (quickfireModel.playingFreeGames) {
            const remaining = quickfireModel.freeGames[0].gamesRemaining;
            const total = quickfireModel.freeGames[0].gamesAwarded;

            this.updateRemainingCounter(remaining, total);

            if (remaining === 0) {
                Services.autoplay.stop();
            }
        }
    }

    public spin() {
        if (quickfireModel.playingFreeGames) {
            const remaining = quickfireModel.freeGames[0].gamesRemaining;
            const fakeRemaining = Math.max(0, remaining - 1);

            const total = quickfireModel.freeGames[0].gamesAwarded;

            this.updateRemainingCounter(fakeRemaining, total);
        }
    }

    public discardPrompt(recover: boolean) {
        return new Contract<boolean>((resolve) => {

            SmartUIFlags.set(SmartUIFlag.PROMPT, true);

            const freeGame = quickfireModel.freeGames[0];

            const promptContent = new PromptContent(["freegames_discard"], Infinity);
            const promptContract = Components.prompt.showPrompt(promptContent);

            promptContent.setText("offer_id", `ID ${freeGame.offerId}-${freeGame.instanceId}`);

            // Button actions
            promptContent.addContinueButton();

            const cancel = () => {
                this.expireDuringPromptSafeguardOff();
                SmartUIFlags.set(SmartUIFlag.PROMPT, false);
                promptContent.hide().execute();
                promptContract.cancel();
            };
            this.expireDuringPromptSafeguardOn(cancel, () => resolve(false));

            const close = () => {
                cancel();
                resolve(false);
            };

            promptContent.addButton("cancel", close);

            promptContract.then(() => {
                cancel();
                this.discard();
                if (quickfireModel.pendingFreeGamesOffers() === 0) {
                    resolve(true);
                } else {
                    this.intro().then(() => resolve(true));
                }
            });
        });
    }

    private updateRemainingCounter(remaining: number, total: number) {
        this.layer.getFlexiText("count").setText(`${remaining} / ${total}`);
    }

    private start() {
        SmartUIFlags.set(SmartUIFlag.PROMPT, false);

        const freeGames = quickfireModel.freeGames[0];
        quickfireModel.playingFreeGames = true;
        GameState.autoDeductStake = false;

        this.layer.setScene("base").execute();

        this.update();

        this.savedCreditSizes = BetModel.creditSizes.clone();
        this.savedCreditsPerLine = BetModel.creditsPerLine.clone();

        const freeGamesBet = new ControlOptions();
        freeGamesBet.creditSizes = BetModel.creditSizes;
        freeGamesBet.creditsPerLine = BetModel.creditsPerLine;
        freeGamesBet.creditSizes.currentValue = freeGames.chipSize;
        freeGamesBet.creditsPerLine.currentValue = freeGames.numChips;

        BetModel.freeBet = true;
        BetModel.updateFromOptions(freeGamesBet);

        smartUiFlags.set(SmartUIFlag.FREE_BETS, true);

        SmartUIManager.refresh();
    }

    private expire() {
        return new Contract<void>((resolve) => {
            SmartUIFlags.set(SmartUIFlag.PROMPT, true);

            const freeGame = quickfireModel.freeGames[0];

            this.end();

            const promptContent = new PromptContent(["freegames_expire"], Infinity);
            const promptContract = Components.prompt.showPrompt(promptContent);

            // Populate text contents
            promptContent.setText("expire", Services.translations.get("qfg.exp", { 0: freeGame.offerId, 1: freeGame.instanceId }));

            // Button actions
            promptContent.addContinueButton();

            const cancel = () => {
                SmartUIFlags.set(SmartUIFlag.PROMPT, false);
                promptContent.hide().execute();
                promptContract.cancel();
            };

            promptContract.then(() => {
                cancel();

                if (quickfireModel.pendingFreeGamesOffers() > 0) {
                    this.freeGamesPopup("freegames_intro").then(() => resolve());
                } else {
                    resolve();
                }
            });
        });
    }

    private summary() {
        return new Contract<boolean>((resolve) => {
            SmartUIFlags.set(SmartUIFlag.PROMPT, true);

            const freeGame = quickfireModel.freeGames[0];

            this.end();

            let sceneName;

            const totalWin = freeGame.sumWinnings;
            if (totalWin > 0) {
                sceneName = "freegames_summary_win";
            } else {
                sceneName = "freegames_summary_lose";
            }
            if (quickfireModel.pendingFreeGamesOffers()) {
                sceneName += "_next";
            }

            const promptContent = new PromptContent([sceneName], Infinity);
            const promptContract = Components.prompt.showPrompt(promptContent);

            // Populate text contents
            if (totalWin > 0) {
                promptContent.setText("value", Services.currency.format(totalWin));

                const isCash = freeGame.creditType === QuickfireCreditType.CASH;
                promptContent.setText("balance_message", Services.translations.get(isCash ? "qfg.in4a" : "qfg.in4b"));
                if (typeof freeGame.creditType === 'undefined') {
                    promptContent.setText("balance_message", "");
                }
            }

            // Button actions
            promptContent.addContinueButton();

            const cancel = () => {
                SmartUIFlags.set(SmartUIFlag.PROMPT, false);
                promptContent.hide().execute();
                promptContract.cancel();
            };

            const close = () => {
                cancel();
                resolve(false);
            };

            if (quickfireModel.pendingFreeGamesOffers()) {
                promptContent.addButton("cancel", () => {
                    cancel();
                    this.intro().then(() => resolve(true));
                });
            }

            promptContract.then(close);
        });
    }

    private checkComplete() {
        const discardButton = this.layer.getFlexiButton("qfg_discard");
        if (discardButton) {
            if ((SmartUIFlags.bitmask & SmartUIFlag.FREE_SPINS) !== 0) {
                discardButton.setVisible(false);
            } else {
                discardButton.setVisible(true);
            }
        }

        const currentExpired = this.checkCurrentExpired();
        const idleAfterRecovery = (SmartUIFlags.bitmask === SmartUIFlag.IDLE) && GameState.getCurrentGame().isRecovered;
        const idleInFreeGames = SmartUIFlags.bitmask === (SmartUIFlag.IDLE | SmartUIFlag.FREE_BETS);

        if (idleInFreeGames || idleAfterRecovery) {
            if (currentExpired) {
                // Current offer has expired
                this.expire().execute();
            } else if (quickfireModel.playingFreeGames && quickfireModel.freeGames[0].gamesRemaining === 0) {
                // Complete
                this.summary().execute();
            } else if (quickfireModel.showFreeGameOffers) {
                // Show intro after recovery
                quickfireModel.nonFreeGamesRecoveryCompleted = true;
                quickfireModel.showFreeGameOffers = false;
                if (quickfireModel.pendingFreeGamesOffers()) {
                    this.freeGamesPopup("freegames_intro").execute();
                }
            } else {
                Timer.setTimeout(() => this.checkComplete(), 1000);
            }
        } else {
            this.expireDuringPromptSafeguardOff();
        }
    }

    private end() {

        smartUiFlags.set(SmartUIFlag.FREE_BETS, false);

        BetModel.freeBet = false;
        if (this.savedCreditSizes) {
            const options: ControlOptions = new ControlOptions();
            options.creditSizes = this.savedCreditSizes;
            options.creditsPerLine = this.savedCreditsPerLine;
            BetModel.updateFromOptions(options);
        }

        GameState.autoDeductStake = true;

        quickfireModel.playingFreeGames = false;
        quickfireModel.freeGames.shift();

        this.layer.defaultScene().execute();

        SmartUIManager.refresh();
    }

    private discard() {
        this.onDiscard.dispatch();
        const offer = quickfireModel.freeGames[0];
        const packet = `<Request verbex="RejectFreeGames"><FreeGames><FreeGame offerId="${offer.offerId}" instanceId="${offer.instanceId}"/></FreeGames></Request>`;
        CommsManager.connector.sendRequest(packet).execute();
        this.end();
    }

    private info(recover: boolean) {
        return this.freeGamesPopup("freegames_info", recover);
    }

    private freeGamesPopup(scene: "freegames_intro" | "freegames_info", recover: boolean = false) {
        return new Contract<void>((resolve) => {

            if (recover) {
                scene += "_recover";
            }

            if (quickfireModel.freeGames.length) {
                SmartUIFlags.set(SmartUIFlag.PROMPT, true);
                const freeGame = quickfireModel.freeGames[0];

                const promptContent = new PromptContent([scene], Infinity);
                const promptContract = Components.prompt.showPrompt(promptContent);

                // Populate text contents
                if (scene.indexOf("freegames_info") > -1) {
                    if (!recover) {
                        promptContent.setText("info_1", Services.translations.get("qfg.in1", { 0: freeGame.gamesRemaining }));
                    } else {
                        promptContent.setText("info_1", Services.translations.get(freeGame.gamesRemaining > 1 ? "qfg.xfgrp" : "qfg.xfgr", { 0: freeGame.gamesRemaining }));
                    }
                    promptContent.setText("info_2", Services.translations.get("qfg.in2", { 0: freeGame.endDate }));

                    const numChips = freeGame.numChips;
                    promptContent.setText("info_3", Services.translations.get(numChips > 0 ? "qfg.in3b" : "qfg.in3a", {
                        0: freeGame.activePaylines.length,
                        1: numChips,
                        2: Services.currency.format(freeGame.chipSize)
                    }));

                    const isCash = freeGame.creditType === QuickfireCreditType.CASH;
                    promptContent.setText("info_4", Services.translations.get(isCash ? "qfg.in4a" : "qfg.in4b"));

                } else {
                    promptContent.setText("free_games", Services.translations.get("qfg.xfg", { 0: freeGame.gamesRemaining }));
                }

                if (!recover) {
                    promptContent.setText("offer_count", `1 / ${quickfireModel.pendingFreeGamesOffers()}`);
                } else {
                    promptContent.setText("free_games", Services.translations.get(freeGame.gamesRemaining > 1 ? "qfg.xfgrp" : "qfg.xfgr", { 0: freeGame.gamesRemaining }));
                }

                promptContent.setText("offer_id", `ID ${freeGame.offerId}-${freeGame.instanceId}`);

                // Button actions
                promptContent.addContinueButton();

                const cancel = () => {
                    this.expireDuringPromptSafeguardOff();
                    SmartUIFlags.set(SmartUIFlag.PROMPT, false);
                    promptContent.hide().execute();
                    promptContract.cancel();
                };
                this.expireDuringPromptSafeguardOn(cancel, resolve);

                const close = () => {
                    cancel();
                    resolve(null);
                };

                if (!recover) {
                    promptContent.addButton("cancel", close);
                }

                promptContent.addButton("discard", () => {
                    cancel();
                    this.discardPrompt(recover).then((discarded) => {
                        if (discarded) {
                            resolve(null);
                        } else {
                            this.intro().then(resolve);
                        }
                    });
                });

                promptContent.addButton("info", () => {
                    cancel();

                    if (scene.indexOf("freegames_intro") > -1) {
                        this.info(recover).then(resolve);
                    } else {
                        this.intro().then(resolve);
                    }
                });

                promptContract.then(() => {
                    this.start();
                    resolve(null);
                });
            } else {
                resolve(null);
            }
        });
    }

    /**
     * Edge case where free games expire while you're already in a free games prompt...
     * Turns on checking for if an expire message should replace current prompt
     */
    private expireDuringPromptSafeguardOn(closeCurrentPrompt: Function, resolve: (value: void) => void) {
        if (quickfireModel.playingFreeGames && this.checkCurrentExpired()) {
            this.expire().execute();
        } else if (this.checkAnyExpired()) {
            closeCurrentPrompt();
            this.intro().then(resolve);
        } else {
            // Recheck in a second
            Timer.clearTimeout(this.expireDuringPromptSafeguardTimer);
            this.expireDuringPromptSafeguardTimer = Timer.setTimeout(() => this.expireDuringPromptSafeguardOn(closeCurrentPrompt, resolve), 1000);
        }
    }

    private expireDuringPromptSafeguardOff() {
        Timer.clearTimeout(this.expireDuringPromptSafeguardTimer);
    }

    private checkCurrentExpired() {
        let currentExpired = false;

        for (let index = quickfireModel.freeGames.length - 1; index >= 0; index--) {
            const freeGame = quickfireModel.freeGames[index];
            if (quickfireModel.isFreeGameExpired(freeGame)) {
                if (quickfireModel.playingFreeGames && index === 0) {
                    // Current game has expired
                    currentExpired = true;
                } else {
                    quickfireModel.freeGames.splice(index, 1);
                }
            }
        }

        return currentExpired;
    }

    private checkAnyExpired() {
        let anyExpired = false;

        for (let index = quickfireModel.freeGames.length - 1; index >= 0; index--) {
            const freeGame = quickfireModel.freeGames[index];
            if (quickfireModel.isFreeGameExpired(freeGame)) {
                anyExpired = true;
                quickfireModel.freeGames.splice(index, 1);
            }
        }

        return anyExpired;
    }
}

export default new QuickfireFreeGamesComponent();
